import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, MessageSquareHeart, Send, ThumbsDown, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { formatDate } from './qualityConfig';

const Choice = ({ active, onClick, icon: Icon, label, testId, tone }) => (
    <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${active ? tone : 'bg-background hover:bg-muted'}`}
        data-testid={testId}
        aria-pressed={active}
    >
        <Icon className="h-4 w-4" /> {label}
    </button>
);

export const SubmittedFeedback = ({ feedback, testId }) => (
    <div className="rounded-xl border bg-muted/30 p-4" data-testid={testId}>
        <p className="flex items-center gap-2 text-sm font-medium">
            {feedback.helpful ? <ThumbsUp className="h-4 w-4 text-emerald-600" /> : <ThumbsDown className="h-4 w-4 text-rose-600" />}
            {feedback.helpful ? 'Found it helpful' : 'Not helpful'}
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">· {formatDate(feedback.submitted_at)}</span>
        </p>
        <p className="mt-1.5 text-sm text-muted-foreground">“{feedback.comment}”</p>
    </div>
);

export const TaskFeedbackForm = ({ task, onSubmitted }) => {
    const { api } = useAuth();
    const [helpful, setHelpful] = useState(null);
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/quality-tasks/my/${task.id}/feedback`, { helpful, comment });
            toast.success('Thanks! Your feedback was sent to your Quality reviewer.');
            onSubmitted();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not send feedback');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="rounded-xl border border-violet-200 bg-violet-500/5 p-4 dark:border-violet-800/50" data-testid={`quality-feedback-form-${task.id}`}>
            <p className="flex items-center gap-2 font-medium"><MessageSquareHeart className="h-4 w-4 text-violet-600" /> Were these simulations helpful?</p>
            <div className="mt-3 flex gap-2">
                <Choice active={helpful === true} onClick={() => setHelpful(true)} icon={ThumbsUp} label="Yes" testId={`task-feedback-yes-${task.id}`}
                    tone="border-emerald-400 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" />
                <Choice active={helpful === false} onClick={() => setHelpful(false)} icon={ThumbsDown} label="No" testId={`task-feedback-no-${task.id}`}
                    tone="border-rose-400 bg-rose-500/10 text-rose-700 dark:text-rose-300" />
            </div>
            <AnimatePresence initial={false}>
                {helpful !== null && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <label className="mt-4 block text-sm font-medium" htmlFor={`feedback-${task.id}`}>{helpful ? 'How did they help you?' : 'What could be better?'}</label>
                        <Textarea
                            id={`feedback-${task.id}`}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={helpful ? 'e.g. The objection drill gave me better words for salary pushback' : 'e.g. I wanted a scenario closer to my real calls'}
                            className="mt-2 min-h-[80px] bg-background"
                            data-testid={`task-feedback-comment-${task.id}`}
                        />
                        <Button onClick={submit} disabled={busy || comment.trim().length < 3} className="mt-3 bg-violet-600 text-white hover:bg-violet-700" data-testid={`task-feedback-submit-${task.id}`}>
                            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send feedback
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
